import styled from '@emotion/styled'
import { tokens } from '@news-mono/design-tokens'
import {
    calcRem,
    breakpointMax,
    themedValue,
    fluidTypography,
    Theme,
    colors,
    breakpoint,
} from '../../../../__styling'
import { RibbonOptions } from '../../helpers/BreachWallTypes'
import { PackageThemeProps } from '../BreachWallContent/BreachWallContent'

// ------------------------------------- Single Price Point Components Below -------------------------------------

export const StyledSubscriptionOriginalPrice = styled('h4')<PackageThemeProps>(
    ({ theme, breachOptions }) => [
        {
            fontSize: calcRem(16),
            fontWeight: 700,
            lineHeight: 1.167,
            textDecoration: 'line-through',
            textTransform: 'uppercase',
            margin: 0,
            flex: '0 0 auto', // fixes ie11 column wrap bug

            fontFamily: theme.fonts.sansSerifCond,
            color:
                breachOptions.isRegional && breachOptions.useBreachDealStyle
                    ? tokens.thewest.colors.palette.westblue
                    : theme.sectionValues.primaryColor,
            fontFeatureSettings: "'pnum' on, 'lnum' on",

            [breakpointMax('xs')]: {
                fontSize: calcRem(16),
            },
        },
        // PROMO: EOFY or AFL
        (breachOptions.style === 'eofy' ||
            breachOptions.style === 'footy-lives-here') && {
            textTransform: 'none',
            fontFamily: theme.fonts.serif,
            color: tokens.thewest.colors.text.secondary,
        },
        // PROMO: Black Friday
        breachOptions.style === 'black-friday' && {
            marginTop: calcRem(20),
            fontSize: calcRem(20),
            fontWeight: 400,
            color: colors.thewest.greyElephant,
            textTransform: 'none',
        },
        // PROMO: Summer of Cricket
        breachOptions.style === 'summer-of-cricket' && {
            fontWeight: 400,
            color: colors.black,
            textTransform: 'none',
        },
    ],
)

const themedPriceFluid = (theme: Theme) =>
    themedValue(theme, {
        sevennews: fluidTypography(375, 1000, 24, 28),
        perthnow: fluidTypography(375, 1020, 24, 30),
        thewest: fluidTypography(375, 1220, 24, 24),
        fallback: undefined,
    })

export const StyledSubscriptionPrice = styled('p')<PackageThemeProps>(
    ({ theme, packageOptions, breachOptions }) => [
        {
            ...themedPriceFluid(theme),
            lineHeight: 1.167,
            margin: 0,
            flex: '0 0 auto', // fixes ie11 column wrap bug
            fontSize: calcRem(32),
            fontWeight: 700,
            fontFamily: theme.fonts.sansSerifCond,
            fontFeatureSettings: "'pnum' on, 'lnum' on",
            textTransform: 'uppercase',
            color:
                breachOptions.isRegional && breachOptions.useBreachDealStyle
                    ? tokens.thewest.colors.palette.westblue
                    : theme.sectionValues.primaryColor,
        },
        // PROMO: Black Friday
        breachOptions.style === 'black-friday' && {
            fontSize: calcRem(32),
            fontWeight: 400,
            fontFamily: theme.fonts.body,
            textTransform: 'none',
            color: tokens.thewest.colors.palette.black,
        },
        // THEME: optimise-blue & afl-footy-design-2026
        (packageOptions.theme === 'optimise-blue' ||
            packageOptions.theme === 'afl-footy-design-2026') && {
            fontFamily: theme.fonts.body,
            color: tokens.thewest.colors.palette.black,
            textTransform: 'none',
            fontWeight: 500,
        },
        // PROMO: Summer of Cricket
        breachOptions.style === 'summer-of-cricket' && {
            color: colors.black,
            textTransform: 'none',
        },
    ],
)
export const StyledSubscriptionBillingCycle = styled('p')<PackageThemeProps>(
    ({ theme, packageOptions, breachOptions }) => [
        {
            fontSize: calcRem(14),
            fontWeight: 400,
            lineHeight: 1.167,
            margin: calcRem(theme.margins.sm, 0, theme.margins.sm),
            color:
                packageOptions.theme === 'red'
                    ? colors.thewest.ladybird
                    : packageOptions.theme === 'yellow'
                    ? colors.thewest.sunshineYellow
                    : undefined,
        },
        // THEME: optimise-blue & afl-footy-design-2026
        (packageOptions.theme === 'optimise-blue' ||
            packageOptions.theme === 'afl-footy-design-2026') && {
            fontWeight: 400,
            margin: calcRem(theme.margins.sm, 0, 0, 0),
        },
        // PROMO: Black Friday
        breachOptions.style === 'black-friday' && {
            color: colors.thewest.greyElephant,
        },
    ],
)

export const StyledSubscriptionPriceQualifier = styled('p')<PackageThemeProps>(
    ({ theme, packageOptions, breachOptions }) => [
        {
            fontSize: calcRem(14),
            flex: '0 0 auto', // fixes ie11 column wrap bug
            fontWeight: 700,
            lineHeight: 1.167,
            margin: 0,
            color:
                packageOptions.theme === 'red'
                    ? colors.thewest.ladybird
                    : packageOptions.theme === 'yellow'
                    ? colors.thewest.sunshineYellow
                    : undefined,
        },
        // THEME: optimise-blue & afl-footy-design-2026
        (packageOptions.theme === 'optimise-blue' ||
            packageOptions.theme === 'afl-footy-design-2026') && {
            fontWeight: 400,
            margin: calcRem(theme.margins.sm, 0, 0, 0),
        },
        // THEME: vanishing-cousins, black-friday, west-blue, optimise-blue, afl-footy-design-2026
        (packageOptions.theme === 'vanishing-cousins' ||
            breachOptions.style === 'black-friday' ||
            packageOptions.theme === 'west-blue' ||
            packageOptions.theme === 'optimise-blue' ||
            packageOptions.theme === 'afl-footy-design-2026') && {
            color: colors.black,
        },
        // PROMO: EOFY, AFL or Black Friday
        (breachOptions.style === 'eofy' ||
            breachOptions.style === 'footy-lives-here') && {
            fontSize: calcRem(16),
        },
        // PROMO: Black Friday
        breachOptions.style === 'black-friday' && {
            fontSize: calcRem(16),
            fontWeight: 400,
            marginBottom: calcRem(10),
        },
        // PROMO: EOFY
        breachOptions.style === 'eofy' && {
            marginTop: calcRem(16),
            marginBottom:
                packageOptions.type === 'digital' ? calcRem(16) : undefined,
        },
        // PROMO: AFL
        breachOptions.style === 'footy-lives-here' && {
            color: tokens.thewest.colors.text.secondary,
        },
        // PROMO: Summer of Cricket
        breachOptions.style === 'summer-of-cricket' && {
            color: colors.black,
            textTransform: 'none',
        },
    ],
)

// ------------------------------------- Radio Price Point Components Below -------------------------------------

export const StyledSubscriptionRadioContainer = styled('fieldset')<
    Pick<PackageThemeProps, 'breachOptions'>
>(({ breachOptions }) => [
    {
        // Reset fieldset styles
        margin: 0,
        padding: 0,
        border: 'none',

        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: calcRem(16),
        minHeight: 'auto',

        [breakpoint('xs')]: {
            minHeight: calcRem(28),
        },
    },
    // PROMO: Footy Lives Here
    breachOptions.style === 'footy-lives-here' && {
        gap: calcRem(8),
    },
])

export const StyledSubscriptionRadioRow = styled('div')<
    Pick<PackageThemeProps, 'breachOptions'>
>(({ breachOptions }) => [
    {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: calcRem(6),
        cursor: 'pointer',

        [breakpoint('xs')]: {
            gap: calcRem(8),
        },
    },
    // PROMO: Footy Lives Here
    breachOptions.style === 'footy-lives-here' && {
        border: `1px solid ${colors.thewest.greySeal}`,
        outline: `1px solid transparent`, // Prevent layout shift on active styling
        padding: calcRem(16),
        borderRadius: calcRem(8),

        '&:has(input[type="radio"]:checked)': {
            background: 'rgba(4, 61, 226, 0.06)',
            borderColor: colors.thewest.westblue,
            outlineColor: colors.thewest.westblue,
        },
    },
])

export const StyledSubscriptionRadioInput = styled('input')(({ theme }) => ({
    // Hide the default radio button styles
    appearance: 'none',

    // Apply custom styles
    cursor: 'pointer',
    width: calcRem(24),
    height: calcRem(24),
    borderRadius: '50%',
    border: `${calcRem(1)} solid ${theme.colors.borders.tertiary}`,
    display: 'grid',
    placeContent: 'center',

    // Style the check mark (hidden unless checked)
    '&::before': {
        content: `''`,
        width: calcRem(16),
        height: calcRem(16),
        borderRadius: '50%',
        transform: 'scale(0)',
    },

    '&:checked': {
        border: `${calcRem(1)} solid ${theme.colors.brand}`,

        // Show the check mark when checked
        '&::before': {
            transform: 'scale(1)',
            backgroundColor: theme.colors.brand,
        },
    },
}))
type BreachOptions = Pick<PackageThemeProps, 'breachOptions'>
export const StyledSubscriptionRadioInputLabel = styled('label')<BreachOptions>(
    ({ breachOptions }) => [
        {
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            gap: calcRem(6),

            [breakpoint('xs')]: {
                gap: calcRem(8),
            },
        },
        // PROMO: Footy Lives Here
        breachOptions.style === 'footy-lives-here' && {
            flexDirection: 'column',
            alignItems: 'flex-start',
            color: colors.thewest.greyElephant,
            gap: calcRem(4),

            [breakpoint('xs')]: {
                gap: calcRem(4),
            },
        },
    ],
)

export const StyledSubscriptionRadioPrice = styled('p')<BreachOptions>(
    ({ theme, breachOptions }) => [
        {
            color: colors.thewest.lightBlack,

            padding: 0,
            margin: 0,

            fontFamily: theme.fonts.sansSerif,
            fontWeight: 500,
            fontSize: calcRem(16),
            lineHeight: calcRem(18),

            [breakpoint('xs')]: {
                fontSize: calcRem(18),
                lineHeight: calcRem(20),
            },
        },
        // PROMO: Footy Lives Here
        breachOptions.style === 'footy-lives-here' && {
            fontWeight: 500,
            color: colors.thewest.greyElephant,
            fontSize: calcRem(14),
            lineHeight: calcRem(16),

            [breakpoint('xs')]: {
                fontSize: calcRem(14),
                lineHeight: calcRem(16),
            },
        },
    ],
)

export const StyledSubscriptionRadioQualifier = styled('p')(({ theme }) => ({
    color: colors.thewest.greyElephant,

    padding: 0,
    margin: 0,
    paddingTop: calcRem(2),

    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',

    fontFamily: theme.fonts.sansSerif,
    fontWeight: 400,
    fontSize: calcRem(14),
    lineHeight: calcRem(16),

    [breakpoint('xs')]: {
        fontSize: calcRem(13),
        lineHeight: calcRem(14),
    },
}))

export const StyledSubscriptionRadioPackageTitle = styled('h5')(
    ({ theme }) => ({
        color: colors.thewest.lightBlack,

        padding: 0,
        margin: 0,
        paddingTop: calcRem(2),

        display: 'flex',
        justifyContent: 'center',

        fontFamily: theme.fonts.sansSerif,
        fontWeight: 600,
        fontSize: calcRem(18),
        lineHeight: calcRem(20),
    }),
)

export const StyledSubscriptionRadioRibbon = styled('p')<{
    ribbonOptions: RibbonOptions
}>(({ theme, ribbonOptions }) => [
    {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: calcRem(4, 8),
        margin: 0,

        fontFamily: theme.fonts.sansSerif,
        fontSize: calcRem(12),
        fontWeight: 500,
        lineHeight: calcRem(16),
    },
    // RIBBON OPTION: Blue Pill
    ribbonOptions === 'bluePill' && {
        borderRadius: calcRem(24),
        backgroundColor: colors.thewest.westblue,
        color: colors.white,
    },
    // RIBBON OPTION: Blue Pill Light
    ribbonOptions === 'bluePillLight' && {
        borderRadius: calcRem(20),
        background: 'rgba(4, 61, 226, 0.06)',
        color: colors.thewest.westblue,
        padding: calcRem(6, 12),
        fontSize: calcRem(14),
        fontWeight: 600,
        maxHeight: calcRem(32),
    },
])
